import { setReflowGroupsAction } from 'editor/src/store/design/slice';
import { ElementAddress, MediaText } from 'editor/src/store/design/types';
import getReflowTextStyleKey from 'editor/src/store/design/util/getReflowTextStyleKey';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';
import type { Thunk } from 'editor/src/store/hooks';

import { extractTextProperties } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

import { cloneGroups } from './reflowGroupOperations';

/** Keep imported layout widths as the baseline when an edit introduces frame padding. */
const calibrateReflowTextWidthOperation =
  (address: ElementAddress, element: MediaText, update: Partial<MediaText>): Thunk =>
  (dispatch, getState) => {
    const groups = getState().design.designData?.spreads[address.spreadIndex]?.reflowGroups;
    if (
      !groups?.length ||
      !element.extra.flexibleWidth ||
      !element.extra.text.trim() ||
      !Number.isFinite(update.width) ||
      !isFontFileLoaded(element.extra.fontFamily)
    ) {
      return;
    }
    const style = getReflowTextStyleKey(element);
    if (style !== getReflowTextStyleKey({ ...element, ...update })) {
      return;
    }
    const needsCalibration = (row: (typeof groups)[number]['rows'][number]) =>
      row.horizontalReflow &&
      row.elementUuids.includes(element.uuid) &&
      !row.dividerUuids?.includes(element.uuid) &&
      row.elementWidthOffsets?.[String(element.uuid)]?.style !== style;
    if (!groups.some((group) => group.rows.some(needsCalibration))) {
      return;
    }
    const measured = extractTextProperties(element, undefined).width;
    const value = element.width - measured;
    if (!Number.isFinite(value) || measured <= 0 || element.width <= 0) {
      return;
    }
    const next = cloneGroups(groups);
    next.forEach((group) =>
      group.rows.forEach((row) => {
        if (needsCalibration(row)) {
          row.elementWidthOffsets = {
            ...row.elementWidthOffsets,
            [String(element.uuid)]: { value, style },
          };
        }
      }),
    );
    dispatch(
      setReflowGroupsAction({
        spreadIndex: address.spreadIndex,
        reflowGroups: next,
      }),
    );
  };

export default calibrateReflowTextWidthOperation;
